import BaseService from '../../base/base_service';
import {CLOCK_CONSTS, CLOCK_LOCALIZATION} from './clock_service_consts';
import DOM from '../../utils/dom';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import {TimeSpan} from "@sparkware/app-utils";
import _ from "../../utils/underscore";
import ReconnectHelper, {RetryItem} from "../../utils/reconnect_helper";
import IClockService, {
  ClockServiceInitParams,
  ClockServiceParsedInitParams,
  DateFormattedToRegulationOffset
} from "../../../../app-react/services/time/time.service.interface";

export const RETRY_INTERVALS: RetryItem[] = [
  {durationInSeconds:3, repeatTimes:3},
  {durationInSeconds:30, repeatTimes:0},
];

class ClockService extends BaseService implements IClockService{
  static CLASS: string = 'ClockService';

  private _timeClockInterval: number = null;
  private _serverUpdateInterval: number = null;
  private _timeData: Date = null;
  private _updateOnce: boolean = null;
  private _initParams: ClockServiceParsedInitParams = null;
  private _regulationOffset: number = null;
  private _retryGetClockHelper: ReconnectHelper;

  init(params: ClockServiceInitParams) {
    super.init(params);

    this._setInitParams(params);
    this._subscribe();
  }

  private _setInitParams(params: ClockServiceInitParams): void {
    const clockUpdateFromServerInterval = params.clockUpdateFromServerInterval || CLOCK_CONSTS.DEFAULT_CLOCK_UPDATE_FROM_SERVER_INTERVAL;
    this._initParams = {
      getClockUrl: params.getClockUrl,
      clockUpdateFromServerInterval: TimeSpan.parse(clockUpdateFromServerInterval),
    };
    if (params) {
      this._getLocalDate = params.getLocalDate || this._getLocalDate;
    }
  }

  private _subscribe(): void {
    this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, this._onInitComplete);
    this.onSystemEvent(SYSTEM_EVENTS.APP_RESUMED, this._readServerClock);
  }

  private _onInitComplete(): void {
    this._tryReadServerClock();
    if (!this._serverUpdateInterval) {
      this._serverUpdateInterval = DOM.setInterval(() => this._tryReadServerClock(), this._initParams.clockUpdateFromServerInterval.totalMilliseconds);
    }
  }

  private _tryReadServerClock(){
    this._removeRetryReadClockHelper();
    this._retryGetClockHelper = this._createRetryReadClockHelper();
    this._retryGetClockHelper.start();
  }
  private _createRetryReadClockHelper() {
    return ReconnectHelper.create({
      retryFunc: () => this._readServerClock(),
      retryIntervals: RETRY_INTERVALS,
      name: 'CLOCK',
    });
  }

  private _removeRetryReadClockHelper() {
    if (!_.isNullOrUndefined(this._retryGetClockHelper)) {
      this._retryGetClockHelper.stop();
      this._retryGetClockHelper = null;
    }
  }

  private _restartTimerInterval(): void {
    if (this._timeClockInterval) {
      DOM.clearInterval(this._timeClockInterval);
    }
    this._timeClockInterval = DOM.setInterval(() => this._increaseTimer(),
                  CLOCK_CONSTS.TIMER_UPDATE_INTERVAL, 'CLOCK_INCREMENT_INTERVAL_TIMER');
  }

  private _increaseTimer(): void {
    const utcTime = this._timeData.getTime();
    this._timeData = new Date(utcTime + CLOCK_CONSTS.TIMER_UPDATE_INTERVAL);
  }

  private _getClockLocalization(): string {
    return this.serviceFactory.regulationService().getClockLocalization();
  }

  public setServerTime(serverTime: string|number): void {
    this._timeData = new Date(serverTime);
    this._restartTimerInterval();
  }

  public setRegulationOffset(regOffset: number): void {
    this._regulationOffset = regOffset;
  }

  public getCurrentDate(): Date {
    return this._timeData || this._getLocalDate();
  }

  public getCurrentServerDate(): Date {
    return this._timeData;
  }

  public getRegulationOffset(): number {
    return this._regulationOffset;
  }

  private _getLocalDate(): Date {
    return new Date();
  }

  //Convert utc date to its equivalent in regulation timezone.
  public convertToRegulationTimeZone(date: Date): DateFormattedToRegulationOffset {
    date = date || this._getLocalDate();
    const useLocalTime = CLOCK_LOCALIZATION.LOCAL === this._getClockLocalization() || null == this.getRegulationOffset();// if offset not defined -> fallback to local time. Please note offset = 0 is accessible value
    if (!useLocalTime) {
      // convert to regulation date
      const utcTime = date.getTime();
      date = new Date(utcTime + this.getRegulationOffset());
    }
    return {
      date: date,
      isUTC: !useLocalTime
    };
  }

  private _readServerClock(): Promise<any> {
    return this.serviceFactory.http2Service().post(this._initParams.getClockUrl)
      .then(response => {
        this.setServerTime((response as any).DateTimeUtcFormated);
        this.setRegulationOffset((response as any).RegulationOffsetInMilliseconds);
        this._timeUpdatedFromServer();
      }).catch(err => {
        this.logger.error('Failed to retrieve server clock', err);
        throw err;
      });
  }

  private _timeUpdatedFromServer(): void {
    if (!this._updateOnce) {
      Event.fire(SYSTEM_EVENTS.TIME_UPDATED_FROM_SERVER);
      this._updateOnce = true;
    }
  }

  // Decides which parameter to use regular or regulation
  // If exists regulation time use it without any conversion
  // if regulation parameter not excist - use regular and shift local PC timezone
  public formatToRegulationOffset(date: Date, dateFormattedToRegulation: Date): DateFormattedToRegulationOffset {
    let isUTC = false;
    // if we already have date in regulation timezone - use it
    if (!_.isNullOrUndefined(dateFormattedToRegulation)) {
      date = dateFormattedToRegulation;
      isUTC = true;
    }
    return {
      date: date,
      isUTC: isUTC
    };
  }
}
export default ClockService;